/**
 * Motion system — Tregua
 * Scroll reveal, lightweight parallax, and magnetic buttons.
 * transform/opacity only, no dependencies. Respects prefers-reduced-motion.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function initReveal() {
  // Shopify wraps every rendered section in `.shopify-section` automatically —
  // used here as a zero-config hook so every section reveals on scroll without
  // needing per-template edits. Scoped to #MainContent so the persistent header
  // and footer chrome (rendered outside <main> in theme.liquid) are never
  // affected. Explicit [data-reveal] elements (finer-grained, used inside
  // custom sections) are combined with the same observer.
  // The first section commonly holds the LCP element (hero image/heading) —
  // it's excluded from the opacity-0 starting state so paint isn't gated
  // behind JS running; it gets its own lighter-touch treatment via CSS instead.
  const autoSections = document.querySelectorAll('#MainContent .shopify-section:not(:first-child)');
  autoSections.forEach((section) => {
    if (!section.hasAttribute('data-reveal')) {
      section.setAttribute('data-reveal', 'fade');
    }
  });

  const items = document.querySelectorAll('[data-reveal]');
  if (!items.length) return;

  if (reducedMotion) {
    items.forEach((item) => item.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.18, rootMargin: '0px 0px -8% 0px' },
  );

  items.forEach((item) => observer.observe(item));
}

function initMagnetic() {
  if (reducedMotion) return;

  // `.button` / `.button-custom` / `.button-secondary` are Horizon's own
  // stable classes (see snippets/button.liquid) — hooking into them directly
  // applies the effect to every themed CTA site-wide with no template edits.
  const buttons = document.querySelectorAll(
    'a.button, a.button-custom, a.button-secondary, [data-magnetic]',
  );
  if (!buttons.length) return;

  const strength = 0.28;
  const maxOffset = 14;

  buttons.forEach((button) => {
    if (button.dataset.magneticBound) return;
    button.dataset.magneticBound = 'true';
    let frame = null;

    button.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'touch') return;
      const rect = button.getBoundingClientRect();
      const x = event.clientX - rect.left - rect.width / 2;
      const y = event.clientY - rect.top - rect.height / 2;
      const offsetX = Math.max(-maxOffset, Math.min(maxOffset, x * strength));
      const offsetY = Math.max(-maxOffset, Math.min(maxOffset, y * strength));

      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        button.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
      });
    });

    button.addEventListener('pointerleave', () => {
      if (frame) cancelAnimationFrame(frame);
      button.style.transform = '';
    });
  });
}

function initParallax() {
  if (reducedMotion) return;

  // Hero media gets a gentle default parallax without requiring a liquid edit —
  // `.hero__media` is Horizon's own stable class for the section's image/video.
  document.querySelectorAll('.hero-wrapper .hero__media').forEach((el) => {
    if (!el.closest('[data-parallax]') && !el.dataset.parallax) {
      el.dataset.parallax = '0.1';
    }
  });

  const elements = document.querySelectorAll('[data-parallax]');
  if (!elements.length) return;

  const active = new Set();
  let ticking = false;

  const update = () => {
    ticking = false;
    for (const el of active) {
      const speed = parseFloat(el.dataset.parallax) || 0.15;
      const rect = el.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const elementCenter = rect.top + rect.height / 2;
      const distanceFromCenter = elementCenter - viewportCenter;
      el.style.transform = `translateY(${distanceFromCenter * speed * -1}px)`;
    }
  };

  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        active.add(entry.target);
      } else {
        active.delete(entry.target);
        entry.target.style.transform = '';
      }
    }
    requestUpdate();
  });

  elements.forEach((el) => observer.observe(el));
  window.addEventListener('scroll', requestUpdate, { passive: true });
}

/**
 * Maps anchor ids to a distinctive substring of their section's heading.
 * templates/index.json carries a Shopify auto-generated warning that its
 * contents "may be updated by the Shopify admin theme editor or related
 * systems" — and in practice the anchor_id setting on these sections has
 * been silently dropped by exactly that, more than once, even though
 * nothing else in the file changed. Rather than trust that setting to
 * survive, this assigns the id at runtime by matching on the heading text,
 * which is real content and far less likely to be silently reset.
 */
const ANCHOR_HEADING_MAP = [
  { id: 'beneficios', match: 'Pensada para tu rutina' },
  { id: 'como-funciona', match: 'Diez minutos para volver a ti' },
  { id: 'preguntas', match: 'Lo que la gente nos pregunta' },
];

function ensureAnchorIds() {
  ANCHOR_HEADING_MAP.forEach(({ id, match }) => {
    if (document.getElementById(id)) return;
    const heading = Array.from(document.querySelectorAll('#MainContent h2')).find((h) =>
      h.textContent?.trim().startsWith(match),
    );
    const section = heading?.closest('.shopify-section');
    if (section) section.id = id;
  });
}

function scrollToHashTarget(hash) {
  if (!hash || hash.length < 2) return;
  let target;
  try {
    target = document.querySelector(hash);
  } catch {
    return;
  }
  if (!target) return;
  target.scrollIntoView({ behavior: reducedMotion ? 'instant' : 'smooth', block: 'start' });
}

function initAnchorScroll() {
  // Horizon's cross-document View Transitions (layout/theme.liquid opts in via
  // `@view-transition { navigation: auto }`) can swallow the browser's native
  // scroll-to-fragment on navigations to a URL like /#shopify-section-<id> —
  // the URL updates but the viewport stays put. This takes scrolling into our
  // own hands instead of relying on native/transition behavior:
  // 1. On load, if the URL already has a matching hash, scroll to it.
  // 2. On click of any same-page link to a #shopify-section-* anchor, scroll
  //    explicitly rather than letting the browser (and the transition) try.

  if (location.hash) {
    // Give layout (fonts, images, section-hydration) a beat to settle before
    // measuring position — otherwise the target's offset can be measured too
    // early and land short.
    requestAnimationFrame(() => setTimeout(() => scrollToHashTarget(location.hash), 60));
  }

  document.addEventListener(
    'click',
    (event) => {
      // header-menu's overflow ("More") flyout renders inside a shadow root
      // (see assets/header-menu.js, `overflowMenu` reads `refs.overflowMenu
      // .shadowRoot`). Clicks originating inside a shadow tree get retargeted
      // so `event.target` is the shadow HOST, not the actual <a> — `closest()`
      // on event.target then never finds the link and this handler silently
      // no-ops, leaving the browser's native (broken) fragment scroll to run.
      // composedPath() walks the real path, shadow boundaries included.
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
      const link = path.find((el) => el instanceof HTMLAnchorElement && el.hasAttribute('href'));
      if (!link) return;

      const url = new URL(link.href, location.href);
      if (!url.hash || url.pathname !== location.pathname || url.origin !== location.origin) return;
      if (!document.querySelector(url.hash)) return;

      event.preventDefault();
      history.pushState(null, '', url.hash);
      scrollToHashTarget(url.hash);
    },
    { capture: true },
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    ensureAnchorIds();
    initReveal();
    initMagnetic();
    initParallax();
    initAnchorScroll();
  });
} else {
  ensureAnchorIds();
  initReveal();
  initMagnetic();
  initParallax();
  initAnchorScroll();
}
